import { useLocation } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { usePoints } from '../../../helpers/hooks/pointsContext';
import { Spinner } from '../../../helpers/UI/Spinner/Spinner';
import { useAuth } from '../../../auth/Auth';
import { RewardsNav } from './components/RewardsNav';
import { EarnHero } from './components/EarnHero';
import { EarnSection } from './sections/EarnSection';
import { ContestSection } from './sections/ContestSection';
import { ReferralsSection } from './sections/ReferralsSection';
import { SocialSubmissionsSection } from './sections/SocialSubmissionsSection';
import { REWARDS_NAV_ITEMS } from './rewardsPageConstants';
import type { EarnFilter } from './rewardsPageTypes';
import { getSectionByPath } from './rewardsPageUtils';
import './RewardsEarn.css';
import { useContestTime } from './hooks/useContestTime';
import { usePointTaskStats } from './hooks/usePointTaskStats';
import { useContestStats } from './hooks/useContestStats';
import { useContestLeaderboard } from './hooks/useContestLeaderboard';
import { EARN_EVENTS_BY_CATEGORY } from '../earnEventsCatalog';

const sectionVariant = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] } },
} as const;

const RewardsPage: React.FC = () => {
  const { points, loading } = usePoints();
  const { pathname } = useLocation();
  const activeSection = useMemo(() => getSectionByPath(pathname), [pathname]);
  const { auth } = useAuth();

  const tickets = Number(points?.data?.tickets?.totalAvailable ?? 0);
  const totalPoints = Number(points?.data?.points?.totalAvailable ?? 0);
  const { countdown: drawCountdown } = useContestTime({
    token: auth?.data.token,
    enabled: pathname.startsWith('/rewards'),
    mode: 'end',
  });
  const isEarnOrReferrals = activeSection === 'earn' || activeSection === 'referrals' || activeSection === 'social';

  const [earnFilter, setEarnFilter] = useState<EarnFilter>('all');
  const [earnExpandedCat, setEarnExpandedCat] = useState<string | null>(
    () => Object.keys(EARN_EVENTS_BY_CATEGORY)[0] ?? null
  );

  const { stats: pointStatsEarn, loading: pointStatsEarnLoading } = usePointTaskStats({
    token: auth?.data.token,
    enabled: activeSection === 'earn',
  });

  const { stats: contestStats, loading: contestStatsLoading } = useContestStats({
    token: auth?.data.token,
    enabled: activeSection === 'contest',
  });

  const {
    items: contestLeaderboard,
    loading: contestLeaderboardLoading,
    meta: contestLeaderboardMeta,
  } = useContestLeaderboard({
    token: auth?.data.token,
    enabled: activeSection === 'contest',
    limit: 10,
    includeOnlyLatestContest: true,
  });

  if (loading) return <Spinner />;

  return (
    <div className='text-main'>
      <motion.div
        className='pg'
        id={isEarnOrReferrals ? 'pg-earn' : undefined}
        initial='hidden'
        animate='show'
        variants={{
          hidden: {},
          show: { transition: { staggerChildren: 0.12, delayChildren: 0.02 } },
        }}
      >
        {isEarnOrReferrals && (
          <motion.div variants={sectionVariant}>
            <EarnHero tickets={tickets} points={totalPoints} countdown={drawCountdown} />
          </motion.div>
        )}

        {isEarnOrReferrals && (
          <motion.div variants={sectionVariant}>
            <RewardsNav items={REWARDS_NAV_ITEMS} pathname={pathname} activeSection={activeSection} />
          </motion.div>
        )}

        {isEarnOrReferrals && (
          <motion.div variants={sectionVariant} className='en-body'>
            {activeSection === 'earn' && (
              <EarnSection
                earnFilter={earnFilter}
                setEarnFilter={setEarnFilter}
                earnExpandedCat={earnExpandedCat}
                setEarnExpandedCat={setEarnExpandedCat}
                pointStats={pointStatsEarn}
                pointTasksLoading={pointStatsEarnLoading}
              />
            )}

            {activeSection === 'referrals' && <ReferralsSection />}

            {activeSection === 'social' && <SocialSubmissionsSection />}
          </motion.div>
        )}

        {activeSection === 'contest' && (
          <motion.div variants={sectionVariant}>
            <ContestSection
              drawCountdown={drawCountdown}
              tickets={tickets}
              contestStats={contestStats}
              contestStatsLoading={contestStatsLoading}
              contestLeaderboard={contestLeaderboard}
              contestLeaderboardLoading={contestLeaderboardLoading}
              contestLeaderboardMeta={contestLeaderboardMeta}
            />
          </motion.div>
        )}

      </motion.div>
    </div>
  );
};

export default RewardsPage;
